import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../config/supabase'
import { MenuItem, VisibilitySchedule } from '../types'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const toInput = (mins: number) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
const fromInput = (v: string) => {
  const [h, m] = v.split(':').map(Number)
  return h * 60 + m
}

interface Props {
  item: MenuItem
  onClose: () => void
  onSaved: () => void
}

export default function ScheduleModal({ item, onClose, onSaved }: Props) {
  const current = item.visibility_schedule
  const [days, setDays] = useState<number[]>(current?.days ?? [])
  const [mode, setMode] = useState<'hours' | 'custom'>(current?.mode ?? 'hours')
  const [start, setStart] = useState(toInput(current?.start ?? 660))
  const [end, setEnd] = useState(toInput(current?.end ?? 780))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggleDay = (d: number) =>
    setDays(prev => (prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]))

  const timesBad = mode === 'custom' && (!start || !end || fromInput(end) <= fromInput(start))
  const canSave = days.length > 0 && !timesBad && !saving

  const write = async (patch: { visibility_schedule: VisibilitySchedule | null; is_86?: boolean }) => {
    setSaving(true)
    setError('')
    const { error: err } = await supabase.from('menu_items').update(patch).eq('id', item.id)
    setSaving(false)
    if (err) {
      setError("That didn't save. Please try again.")
      return
    }
    onSaved()
    onClose()
  }

  const save = () => {
    const schedule: VisibilitySchedule = mode === 'custom'
      ? { days, mode, start: fromInput(start), end: fromInput(end) }
      : { days, mode }
    // A taken-off item would never appear on its schedule, so saving puts it back on.
    write({ visibility_schedule: schedule, ...(item.is_86 ? { is_86: false } : {}) })
  }

  const segment = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '10px 8px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
    background: active ? 'var(--gold)' : 'transparent',
    color: active ? 'var(--black)' : 'var(--gray)',
  })

  const timeInput: React.CSSProperties = {
    width: '100%', minWidth: 0, boxSizing: 'border-box', padding: '10px 6px', borderRadius: 8, textAlign: 'center',
    border: '1px solid var(--border)', background: 'var(--dark-input)',
    color: 'var(--white)', fontSize: 14, outline: 'none', colorScheme: 'dark',
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1600,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`When ${item.name} is visible`}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', boxSizing: 'border-box',
          background: '#161616', border: '1px solid var(--border)', borderRadius: 16,
          maxWidth: 380, width: '100%', padding: 22,
        }}
      >
        <button
          onClick={onClose}
          aria-label="Cancel"
          style={{
            position: 'absolute', top: 14, right: 14, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer',
          }}
        >
          <X size={20} />
        </button>

        <h3 style={{ color: 'var(--white)', fontSize: 18, fontWeight: 700, margin: '0 36px 2px 0' }}>
          When is this visible?
        </h3>
        <p style={{ color: 'var(--gray)', fontSize: 14, margin: '0 36px 18px 0' }}>{item.name.trim()}</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 16 }}>
          {DAYS.map((label, d) => {
            const on = days.includes(d)
            return (
              <button
                key={label}
                onClick={() => toggleDay(d)}
                aria-pressed={on}
                style={{
                  padding: '11px 0', minWidth: 0, borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600,
                  border: on ? '1px solid var(--gold)' : '1px solid var(--border)',
                  background: on ? 'var(--gold)' : 'transparent',
                  color: on ? 'var(--black)' : 'var(--white)',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>

        <div style={{ display: 'flex', gap: 4, padding: 4, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 12 }}>
          <button onClick={() => setMode('hours')} aria-pressed={mode === 'hours'} style={segment(mode === 'hours')}>Open to close</button>
          <button onClick={() => setMode('custom')} aria-pressed={mode === 'custom'} style={segment(mode === 'custom')}>Set times</button>
        </div>

        {mode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <input className="schedule-time" type="time" aria-label="Start time" value={start} onChange={e => setStart(e.target.value)} style={timeInput} />
            <span style={{ color: 'var(--gray)', fontSize: 14, flexShrink: 0 }}>to</span>
            <input className="schedule-time" type="time" aria-label="End time" value={end} onChange={e => setEnd(e.target.value)} style={timeInput} />
          </div>
        )}

        {timesBad && (
          <p style={{ color: '#f59e0b', fontSize: 13, margin: '0 0 12px' }}>The end time needs to be after the start time.</p>
        )}
        {item.is_86 && (
          <p style={{ color: 'var(--gray)', fontSize: 13, lineHeight: 1.5, margin: '0 0 12px' }}>Saving also puts this back on the menu.</p>
        )}
        {error && (
          <p style={{ color: '#ef4444', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
        )}

        <button
          onClick={save}
          disabled={!canSave}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none',
            background: 'var(--gold)', color: 'var(--black)', fontSize: 15, fontWeight: 700,
            cursor: canSave ? 'pointer' : 'default', opacity: canSave ? 1 : 0.4,
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>

        {current && (
          <button
            onClick={() => write({ visibility_schedule: null })}
            disabled={saving}
            style={{
              width: '100%', marginTop: 8, padding: '10px 16px', borderRadius: 10, border: 'none',
              background: 'transparent', color: 'var(--gray)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Always show
          </button>
        )}
      </div>
    </div>
  )
}
